import { Image } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { PimLink } from '@/features/shared/pim-link';
import type { Product } from '@/types/core/product/product';

export type ProductHoverCardProps = {
  product: Product;
};

export function ProductHoverCard({
  product,
}: ProductHoverCardProps): ReactElement {
  const { t } = useTranslation('common');
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const displayTitle = product.title ?? String(product.id);

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <span className='cursor-pointer hover:underline'>{product.id}</span>
      </HoverCardTrigger>
      <HoverCardContent className='w-100'>
        <div className='flex items-center space-x-2'>
          {product.image ? (
            <div className='relative h-20 min-w-24'>
              {!isImageLoaded && (
                <Skeleton className='absolute inset-0 h-20 min-w-24 rounded-md' />
              )}
              {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: onLoad is for detecting image load completion */}
              <img
                alt={displayTitle}
                className={`h-20 min-w-24 rounded-md object-cover object-center transition-opacity ${isImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                height={80}
                loading='lazy'
                onLoad={() => setIsImageLoaded(true)}
                src={product.image}
                width={80}
              />
            </div>
          ) : (
            <div className='flex h-16 w-16 items-center justify-center rounded-md bg-accent'>
              <Image className='h-8 w-8 text-muted-foreground' />
            </div>
          )}
          <span className='p-2 text-sm'>{displayTitle}</span>
        </div>
        <div className='flex justify-end'>
          <PimLink
            className='text-blue-500 text-sm italic hover:underline'
            href={`/products/${product.id}`}
          >
            {t('link to pim')}
          </PimLink>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
